import {
  Dimensions,
  ExtraBase,
  RuleActionApiModel,
  RuleApiModel,
  RuleConditionApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { MarketplaceService } from '../../../lib/consts/ItemMetaConstants';
import { ToNumber } from '../../../lib/helpers/ArrayHelper';
import { uid } from '../../../lib/helpers/uid';
import {
  CoverExtra,
  DeliveryGuaranteeExtra,
  ExtendedBaseCoverExtra,
  PrintInStoreExtra,
  SignatureExtra,
  SmsExtra
} from '../../ExtrasHelper';
import { AllowedInlineConditions } from '../edit/rules/AllowedInlineConditions';
import { Actions } from './Actions';
import { ActionType } from './ActionType';
import { Comparisons } from './Comparisons';
import { Conditions } from './Conditions';
import { Operators } from './Operators';
import { SortFilterCollator } from './SortFilterCollator';

export class RuleNames {
  static DomesticService = 'Domestic Service';
  static LabelReference = 'Label Reference';
  static InternationalService = 'International Service';
  static Email = 'Email';
  static Phone = 'Phone';
  static Extras = 'Extras';
  static Weight = 'Weight';
  static MarketplaceServices = 'Marketplace Services';
  static Dimensions = 'Dimensions';
  static Tag = 'Default Rule';
  static InlineTag = 'Item Rule';
}

export function generateDefaultDomesticServiceRule(serviceSlug: string, domesticIsoCode: string): RuleApiModel {
  return {
    Tags: [RuleNames.Tag],
    RuleType: 'SmartSend',
    Actions: [
      {
        Field: Actions.Item_Service,
        Type: ActionType.Replace,
        Value: serviceSlug,
        Id: uid()
      }
    ],
    Active: true,
    BreakOnConditionMet: false,
    ConditionType: Operators.Or,
    Conditions: [
      {
        Comparison: Comparisons.EqualTo,
        Field: Conditions.Recipient_Country,
        Values: [domesticIsoCode],
        Id: uid()
      }
    ],
    Priority: 0,
    Name: RuleNames.DomesticService,
    Description: 'Applies the selected service for all of your domestic items, where available.'
  } as RuleApiModel;
}

export function generateDefaultInternationalServiceRule(serviceSlug: string, domesticIsoCode: string): RuleApiModel {
  return {
    Tags: [RuleNames.Tag],
    RuleType: 'SmartSend',
    Actions: [
      {
        Field: Actions.Item_Service,
        Type: ActionType.Replace,
        Value: serviceSlug,
        Id: uid()
      }
    ],
    Active: true,
    BreakOnConditionMet: false,
    ConditionType: Operators.Or,
    Conditions: [
      {
        Comparison: Comparisons.NotEqualTo,
        Field: Conditions.Recipient_Country,
        Values: [domesticIsoCode],
        Id: uid()
      }
    ],
    Priority: 0,
    Name: RuleNames.InternationalService,
    Description: 'Applies the selected service for all of your international items, where available.'
  } as RuleApiModel;
}

export function generateDefaultEmailRule(email: string): RuleApiModel {
  return {
    Tags: [RuleNames.Tag],
    RuleType: 'SmartSend',
    Actions: [
      {
        Field: Actions.Recipient_Email,
        Type: ActionType.Replace,
        Value: email,
        Id: uid()
      }
    ],
    Active: true,
    BreakOnConditionMet: false,
    ConditionType: Operators.Or,
    Conditions: [
      {
        Comparison: Comparisons.IsEmpty,
        Field: Conditions.Recipient_Email,
        Values: [],
        Id: uid()
      }
    ],
    Priority: 0,
    Name: RuleNames.Email,
    Description: 'Applies the selected email where one is missing.'
  } as RuleApiModel;
}

export function generateDefaultPhoneRule(phone: string): RuleApiModel {
  return {
    Tags: [RuleNames.Tag],
    RuleType: 'SmartSend',
    Actions: [
      {
        Field: Actions.Recipient_Phone,
        Type: ActionType.Replace,
        Value: phone,
        Id: uid()
      }
    ],
    Active: true,
    BreakOnConditionMet: false,
    ConditionType: Operators.Or,
    Conditions: [
      {
        Comparison: Comparisons.IsEmpty,
        Field: Conditions.Recipient_Phone,
        Values: [],
        Id: uid()
      }
    ],
    Priority: 0,
    Name: RuleNames.Phone,
    Description: 'Applies the selected phone number where one is missing.'
  } as RuleApiModel;
}

export function generateDefaultWeightRule(weight: number): RuleApiModel {
  return {
    Tags: [RuleNames.Tag],
    RuleType: 'SmartSend',
    Actions: generateWeightActions(weight),
    Active: true,
    BreakOnConditionMet: false,
    ConditionType: Operators.And,
    Conditions: [
      {
        Comparison: Comparisons.LessThanOrEqualTo,
        Field: Conditions.Item_Weight,
        Values: ['0'],
        Id: uid()
      }
    ],
    Priority: 0,
    Name: RuleNames.Weight,
    Description: 'Applies a default weight when no weight is imported.'
  } as RuleApiModel;
}

export function GetWeightFromRule(rule: RuleApiModel): number {
  if (rule?.Actions?.length > 0) {
    return ToNumber(rule.Actions.find((x) => x.Field === Actions.Item_Weight)?.Value);
  }
  return 0;
}

const ruleMatchesInclusiveCondition = (rule: RuleApiModel, condition: string, value: string[]) =>
  !!rule.Conditions.find(
    (x) =>
      x.Field === condition &&
      [Comparisons.Contains, Comparisons.EqualTo, Comparisons.IsOneOf].includes(x.Comparison) &&
      x.Values.find((v) => value.includes(v))
  );

const ruleHasAllActions = (rule: RuleApiModel, actions: string[]) =>
  actions.filter((f) => rule.Actions.find((x) => x.Field === f)).length === actions.length;

export function getRuleConditionsForMatchingActions(
  rule: RuleApiModel,
  item: SmartSendItemApiModel,
  actions: string[]
): string[] {
  if (!ruleHasAllActions(rule, actions)) {
    return [];
  }

  const output: string[] = [];

  if (AllowedInlineConditions.includes(Conditions.Item_Name)) {
    const names = item.MarketplaceOrderLines.map((m) => m.Product.Description).concat([item.Description]);
    if (ruleMatchesInclusiveCondition(rule, Conditions.Item_Name, names)) {
      output.push(Conditions.Item_Name);
    }
  }

  if (AllowedInlineConditions.includes(Conditions.Item_Sku)) {
    const skus = item.MarketplaceOrderLines.map((m) => m.Product.Sku);
    if (ruleMatchesInclusiveCondition(rule, Conditions.Item_Sku, skus)) {
      output.push(Conditions.Item_Sku);
    }
  }

  if (AllowedInlineConditions.includes(Conditions.Item_TariffCode)) {
    const tariffCodes = item.MarketplaceOrderLines.map((m) => m.Product.TariffCode);
    if (ruleMatchesInclusiveCondition(rule, Conditions.Item_TariffCode, tariffCodes)) {
      output.push(Conditions.Item_TariffCode);
    }
  }

  if (AllowedInlineConditions.includes(Conditions.Item_MarketplaceService)) {
    const mps = item.Meta[MarketplaceService]?.Value;
    if (ruleMatchesInclusiveCondition(rule, Conditions.Item_MarketplaceService, [mps])) {
      output.push(Conditions.Item_MarketplaceService);
    }
  }

  if (AllowedInlineConditions.includes(Conditions.Item_Source)) {
    const sources = item.MarketplaceOrderLines.map((m) => m.Source.Id);
    if (ruleMatchesInclusiveCondition(rule, Conditions.Item_Source, sources)) {
      output.push(Conditions.Item_Source);
    }
  }

  return output;
}

export function ruleMatchesActionsAndConditions(rule: RuleApiModel, item: SmartSendItemApiModel, actions: string[]) {
  return getRuleConditionsForMatchingActions(rule, item, actions).length > 0;
}

export function generateWeightActions(weight: number): RuleActionApiModel[] {
  return [
    {
      Field: Actions.Item_Weight,
      Type: ActionType.Replace,
      Value: String(weight),
      Id: uid()
    }
  ];
}

export function GetDimensionsFromRule(rule: RuleApiModel): Dimensions {
  const output: Dimensions = {
    Height: 0,
    Width: 0,
    Length: 0
  };

  if (rule?.Actions?.length > 0) {
    output.Height = ToNumber(rule.Actions.find((x) => x.Field === Actions.Item_Height)?.Value);
    output.Length = ToNumber(rule.Actions.find((x) => x.Field === Actions.Item_Length)?.Value);
    output.Width = ToNumber(rule.Actions.find((x) => x.Field === Actions.Item_Width)?.Value);
  }

  return output;
}

export function generateDimensionsActions(height: number, width: number, length: number): RuleActionApiModel[] {
  const dims = [height, width, length].map(String).sort(SortFilterCollator.compare).reverse();
  return [
    {
      Field: Actions.Item_Length,
      Type: ActionType.Replace,
      Value: dims[0],
      Id: uid()
    },
    {
      Field: Actions.Item_Width,
      Type: ActionType.Replace,
      Value: dims[1],
      Id: uid()
    },
    {
      Field: Actions.Item_Height,
      Type: ActionType.Replace,
      Value: dims[2],
      Id: uid()
    }
  ];
}

export function createNewInlineRule(
  title: string,
  description: string,
  conditions: RuleConditionApiModel[],
  actions: RuleActionApiModel[]
): RuleApiModel {
  return {
    Tags: [RuleNames.InlineTag],
    RuleType: 'SmartSend',
    Actions: actions,
    Active: true,
    BreakOnConditionMet: false,
    ConditionType: Operators.Or,
    Conditions: conditions,
    Priority: 0,
    Name: title,
    Description: description
  };
}

export function generateDefaultDimensionsRule(height: number, width: number, length: number): RuleApiModel {
  return {
    Tags: [RuleNames.Tag],
    RuleType: 'SmartSend',
    Actions: generateDimensionsActions(height, width, length),
    Active: true,
    BreakOnConditionMet: false,
    ConditionType: Operators.And,
    Conditions: [
      {
        Comparison: Comparisons.LessThanOrEqualTo,
        Field: Conditions.Item_Length,
        Values: ['0'],
        Id: uid()
      },
      {
        Comparison: Comparisons.LessThanOrEqualTo,
        Field: Conditions.Item_Width,
        Values: ['0'],
        Id: uid()
      },
      {
        Comparison: Comparisons.LessThanOrEqualTo,
        Field: Conditions.Item_Height,
        Values: ['0'],
        Id: uid()
      }
    ],
    Priority: 0,
    Name: RuleNames.Dimensions,
    Description: 'Applies default dimensions when no dimensions are imported.'
  };
}

export function generateDefaultExtrasRule(extras: ExtraBase[]): RuleApiModel {
  return {
    Tags: [RuleNames.Tag],
    RuleType: 'SmartSend',
    Actions: generateDefaultExtrasRuleActions(extras),
    Active: true,
    BreakOnConditionMet: false,
    ConditionType: Operators.Or,
    Conditions: [],
    Priority: 0,
    Name: RuleNames.Extras,
    Description: 'Applies the selected extras for all of your items, where available.'
  };
}

export function generateDefaultExtrasRuleActions(extras: ExtraBase[]): RuleActionApiModel[] {
  const actions: RuleActionApiModel[] = [];
  extras.forEach((field) => {
    actions.push({
      Field: GetRuleActionFieldFromExtra(field),
      Type: ActionType.Replace,
      Value: 'true',
      Id: uid()
    });
  });
  return actions;
}

export function GetExtrasFromRule(dRule: RuleApiModel): ExtraBase[] {
  const output: ExtraBase[] = [];
  if (dRule.Actions.find((r) => r.Field == Actions.Extra_PrintInStore && r.Value === 'true')) {
    output.push({ Id: PrintInStoreExtra, Name: 'Print In Store' });
  }
  if (dRule.Actions.find((r) => r.Field == Actions.Extra_Signature && r.Value === 'true')) {
    output.push({ Id: SignatureExtra, Name: 'Signature' });
  }
  if (dRule.Actions.find((r) => r.Field == Actions.Extra_Cover && r.Value === 'true')) {
    output.push({ Id: CoverExtra, Name: 'Protection' });
  }
  if (dRule.Actions.find((r) => r.Field == Actions.Extra_ExtendedBaseCover && r.Value === 'true')) {
    output.push({ Id: ExtendedBaseCoverExtra, Name: 'ExtendedBaseCover' });
  }
  if (dRule.Actions.find((r) => r.Field == Actions.Extra_DeliveryGuarantee && r.Value === 'true')) {
    output.push({ Id: DeliveryGuaranteeExtra, Name: 'Delivery Guarantee' });
  }
  if (dRule.Actions.find((r) => r.Field == Actions.Extra_Sms && r.Value === 'true')) {
    output.push({ Id: SmsExtra, Name: 'Sms' });
  }
  return output;
}

export function GetRuleActionFieldFromExtra(extra: ExtraBase): string {
  switch (extra) {
    default:
      return `EXTRA:${extra.Id}`.toUpperCase();
  }
}
